import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Tv, Delete } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';

export default function LandingPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const { fetchByCode, user } = useApp();
  const [code, setCode] = React.useState('');
  const [checking, setChecking] = React.useState(false);

  const push = (d) => setCode((c) => (c.length < 2 ? c + d : c));
  const back = () => setCode((c) => c.slice(0, -1));

  const submit = React.useCallback(async () => {
    if (code.length !== 2 || checking) return;
    setChecking(true);
    const t = await fetchByCode(code);
    setChecking(false);
    if (!t) {
      toast({ title: 'Часы не найдены', description: `Нет турнира с кодом ${code}.`, variant: 'destructive' });
      return;
    }
    nav(`/clock/${code}`);
  }, [code, checking, fetchByCode, nav, toast]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) push(e.key);
      else if (e.key === 'Backspace') back();
      else if (e.key === 'Enter') submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submit]);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      {/* Минимальный верх */}
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between px-4 py-4 sm:px-6">
        <Link to="/" className="flex items-center gap-3">
          <img src="/magnum-logo.png" alt="MAGNUM" className="h-11 w-11 rounded-md object-contain" />
          <div className="leading-tight">
            <div className="text-base font-extrabold tracking-tight text-neutral-900 sm:text-lg">MAGNUM <span className="text-red-600">ведущий дом спортивного покера</span></div>
          </div>
        </Link>
        <Button variant="outline" onClick={() => nav(user ? '/admin' : '/admin/login')} className="border-neutral-300 text-sm">
          {user ? 'Кабинет' : 'Админ'}
        </Button>
      </div>

      {/* Центральный блок */}
      <div className="flex min-h-screen items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md rounded-2xl border-neutral-200 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-red-600">
              <Tv className="h-4 w-4" /> Подключение к часам
            </div>
            <h1 className="mt-2 text-3xl font-extrabold text-neutral-900">Введите 2-значный код</h1>
            <p className="mt-1 text-sm text-neutral-600">Код выдаёт организатор турнира.</p>

            <div className="mt-8 flex justify-center gap-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className={`flex h-16 w-12 items-center justify-center rounded-lg border-2 font-clock text-4xl font-bold ${code[i] ? 'border-red-500 bg-red-50 text-red-700' : 'border-neutral-200 bg-white text-neutral-400'}`}
                >
                  {code[i] || '•'}
                </div>
              ))}
            </div>

            <div className="mx-auto mt-8 grid max-w-xs grid-cols-3 gap-3">
              {['1','2','3','4','5','6','7','8','9'].map((d) => (
                <button key={d} onClick={() => push(d)} className="h-14 rounded-lg border border-neutral-200 bg-white text-2xl font-semibold text-neutral-800 shadow-sm transition-colors hover:bg-neutral-100 active:bg-neutral-200">{d}</button>
              ))}
              <button onClick={back} className="h-14 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-600 shadow-sm transition-colors hover:bg-neutral-100">
                <Delete className="mx-auto h-5 w-5" />
              </button>
              <button onClick={() => push('0')} className="h-14 rounded-lg border border-neutral-200 bg-white text-2xl font-semibold text-neutral-800 shadow-sm transition-colors hover:bg-neutral-100">0</button>
              <button
                onClick={submit}
                disabled={code.length !== 2 || checking}
                className="h-14 rounded-lg bg-red-600 font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:bg-neutral-300"
              >
                {checking ? '…' : <Play className="mx-auto h-5 w-5" />}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
